import { useField } from "formik";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { ActionMeta, OnChangeValue, Select } from "chakra-react-select";
import styles from "./index.module.scss";
import classNames from "classnames";
import { QuestionIcon } from "@chakra-ui/icons";

type Option = { label: string | number; value: string | number };
type Prop = {
  label: string | number;
  options: Option[];
  defaultValue?: Option;
  isMulti?: boolean;
  value?: Option | Option[];
  onChange?: (selected: OnChangeValue<Option, boolean>, actionMeta: ActionMeta<Option>) => void;
  required?: boolean;
  classname?: string;
  children?: React.ReactNode;
  hideLabel?: boolean;
  placeholder?: string;
  id?: string;
  name?: string; // 如果传入 name，则绑定到 Formik，否则使用 value 和 onChange
  description?: string;
};

// Formik 控制的 Select 组件
const FormikSelect = ({ name, options, isMulti, onChange, ...rest }: Prop & { name: string }) => {
  const [field, meta, helper] = useField(name);

  const handleChange = (selected: OnChangeValue<Option, boolean>, actionMeta: ActionMeta<Option>) => {
    if (isMulti && Array.isArray(selected)) {
      helper.setValue(selected.map((option) => option.value));
    } else if (!isMulti && selected) {
      helper.setValue((selected as Option).value);
    }
    if (onChange) {
      onChange(selected, actionMeta);
    }
  };

  const selectValue = isMulti
    ? options.filter((option) => Array.isArray(field.value) && field.value.includes(option.value))
    : options.find((option) => option.value === field.value);

  return (
    <Select
      className={styles.select}
      defaultValue={rest.defaultValue}
      isMulti={isMulti}
      options={options}
      value={selectValue}
      onChange={handleChange}
      placeholder={rest.placeholder}
      noOptionsMessage={() => <span>无数据</span>}
      selectedOptionStyle="check"
      chakraStyles={{
        dropdownIndicator: (provided) => ({
          ...provided,
          bg: "transparent",
          p: 0,
          w: 6,
          mx: 2,
          cursor: "inherit",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
      }}
    />
  );
};

// 非 Formik 控制的 Select 组件
const StandaloneSelect = ({ value, onChange, options, isMulti, ...rest }: Prop) => {
  const handleChange = (selected: OnChangeValue<Option, boolean>, actionMeta: ActionMeta<Option>) => {
    if (onChange) {
      onChange(selected, actionMeta);
    }
  };

  return (
    <Select
      className={styles.select}
      defaultValue={rest.defaultValue}
      isMulti={isMulti}
      options={options}
      value={value}
      onChange={handleChange}
      placeholder={rest.placeholder}
      noOptionsMessage={() => <span>无数据</span>}
      selectedOptionStyle="check"
      chakraStyles={{
        dropdownIndicator: (provided) => ({
          ...provided,
          bg: "transparent",
          p: 0,
          w: 6,
          mx: 2,
          cursor: "inherit",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
      }}
    />
  );
};

const CSelect = ({
  label,
  placeholder,
  hideLabel,
  required,
  onChange,
  options,
  isMulti,
  value,
  defaultValue,
  classname,
  id,
  name,
  description,
}: Prop) => {
  const formId = id || name;

  return (
    <Flex
      width="100%"
      maxWidth="800px"
      justifyContent="space-between"
      className={classNames(styles.container, classname)}
    >
      {!hideLabel && (
        <Flex alignItems="center">
          <Text as="label" htmlFor={formId} className={styles.label}>
            {label}
            {required && (
              <Box color="red" as="span">
                *
              </Box>
            )}
            :
          </Text>
          {description && (
            <Tooltip label={description} aria-label="A tooltip">
              <QuestionIcon cursor="pointer" />
            </Tooltip>
          )}
        </Flex>
      )}
      <Box flexGrow={1} maxWidth="400px">
        {name ? (
          <FormikSelect
            label={label}
            name={name}
            options={options}
            isMulti={isMulti}
            onChange={onChange}
            defaultValue={defaultValue}
            placeholder={placeholder}
            id={id}
            required={required}
            hideLabel={hideLabel}
            classname={classname}
            description={description}
          />
        ) : (
          <StandaloneSelect
            label={label}
            options={options}
            isMulti={isMulti}
            value={value}
            onChange={onChange}
            defaultValue={defaultValue}
            placeholder={placeholder}
            id={id}
            required={required}
            hideLabel={hideLabel}
            classname={classname}
            description={description}
          />
        )}
      </Box>
    </Flex>
  );
};

export default CSelect;
